/**
 * @fileoverview Navbar Component
 *
 * Main navigation component for RSH Portal client interface.
 * Layout:
 * - Desktop: Logo (left) | Nav links (center) | Profile dropdown (right)
 * - Mobile: Logo + Profile (top bar) | 5-item bottom nav
 *
 * The profile dropdown contains "My Profile" and "Sign Out" only.
 * Patterned after AdminNavbar to keep both portals visually and
 * structurally aligned.
 *
 * @component
 * @version 2.0.0
 */

import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { signOut } from "firebase/auth";
import * as Sentry from "@sentry/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHouse } from "@fortawesome/free-regular-svg-icons";
import { faArrowsRotate } from "@fortawesome/free-solid-svg-icons";
import {
  FaRegCalendarAlt,
  FaFileAlt,
  FaUserCircle,
  FaSignOutAlt,
  FaPalette,
  FaChevronDown,
  FaUser,
  FaRoute,
} from "react-icons/fa";
import { useOnboarding } from "../onboarding/OnboardingProvider";
/* eslint-disable-next-line no-unused-vars */
import "./Navbar.css";

/* ============================================
   PROFILE DROPDOWN
   ============================================ */

/**
 * Profile dropdown menu for the client navbar.
 * Contains "My Profile" and a separated "Sign Out" action.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the dropdown is visible
 * @param {Function} props.onClose - Handler to close the dropdown
 * @param {Function} props.onSignOut - Handler for sign out action
 * @param {Function} props.onTakeTour - Handler to replay the current page's tour
 * @returns {JSX.Element|null} Dropdown menu, or null when closed
 */
const ProfileDropdown = ({ isOpen, onClose, onSignOut, onTakeTour }) => {
  const location = useLocation();

  /**
   * Checks if the given path matches the current route.
   * @param {string} path - Route to test
   * @returns {boolean} True if path is active
   */
  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path);
  };

  if (!isOpen) return null;

  return (
    <div className="cn-profile-dropdown">
      <Link
        to="/profile"
        className={`cn-dropdown-item ${isActive("/profile") ? "active" : ""}`}
        onClick={onClose}
      >
        <span className="cn-dropdown-icon">
          <FaUser />
        </span>
        <span className="cn-dropdown-label">My Profile</span>
      </Link>
      <button
        className="cn-dropdown-item"
        onClick={() => {
          onClose();
          onTakeTour();
        }}
      >
        <span className="cn-dropdown-icon">
          <FaRoute />
        </span>
        <span className="cn-dropdown-label">Take a tour</span>
      </button>
      <div className="cn-dropdown-divider" />
      <button
        className="cn-dropdown-item cn-dropdown-signout"
        onClick={() => {
          onClose();
          onSignOut();
        }}
      >
        <span className="cn-dropdown-icon">
          <FaSignOutAlt />
        </span>
        <span className="cn-dropdown-label">Sign Out</span>
      </button>
    </div>
  );
};

/* ============================================
   MAIN NAVBAR
   ============================================ */

/**
 * Main client portal Navbar component.
 *
 * @param {Object} props
 * @param {Object} props.user - Current Firebase auth user
 * @param {Object} props.userData - Current user data from Airtable
 * @param {Object} props.project - Current project data
 * @returns {JSX.Element} Navbar with desktop and mobile layouts
 */
function Navbar({ user, userData, project }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { startCurrentTour } = useOnboarding();
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const profileDesktopRef = useRef(null);
  const profileMobileRef = useRef(null);

  // Close dropdown when clicking outside either profile container
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isInsideDesktop = profileDesktopRef.current?.contains(event.target);
      const isInsideMobile = profileMobileRef.current?.contains(event.target);

      if (!isInsideDesktop && !isInsideMobile) {
        setProfileDropdownOpen(false);
      }
    };

    if (profileDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileDropdownOpen]);

  // Close dropdown on route change
  useEffect(() => {
    setProfileDropdownOpen(false);
  }, [location.pathname]);

  /**
   * Handle user sign out with Sentry cleanup.
   */
  const handleSignOut = async () => {
    try {
      await signOut(auth);
      Sentry.setUser(null);
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
      Sentry.captureException(error);
    }
  };

  /**
   * Check if the given path matches the current route.
   * @param {string} path - Route path to check
   * @returns {boolean} True if path is active
   */
  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path);
  };

  /** Toggle the profile dropdown */
  const toggleProfileDropdown = () => {
    setProfileDropdownOpen((prev) => !prev);
  };

  /** Display name for the profile area */
  const displayName =
    userData?.name ||
    project?.clientName ||
    user?.displayName ||
    user?.email?.split("@")[0] ||
    "Client";

  /** Navigation links shared between desktop and mobile */
  const navLinks = [
    {
      path: "/dashboard",
      icon: <FontAwesomeIcon icon={faHouse} />,
      label: "Dashboard",
    },
    {
      path: "/updates",
      icon: <FaRegCalendarAlt />,
      label: "Updates",
    },
    {
      path: "/selections",
      icon: <FaPalette />,
      label: "Selections",
    },
    {
      path: "/change-orders",
      icon: <FontAwesomeIcon icon={faArrowsRotate} />,
      label: "Changes",
    },
    {
      path: "/documents",
      icon: <FaFileAlt />,
      label: "Documents",
    },
  ];

  return (
    <div className="cn-wrapper">
      {/* ==================== DESKTOP TOP BAR ==================== */}
      <nav className="cn-desktop">
        <div className="cn-desktop-container">
          {/* Logo — left */}
          <Link to="/dashboard" className="cn-logo">
            <img
              src="/rsh-logo-sq.png"
              alt="Red Setter Homes"
              className="cn-logo-image"
            />
            <div className="cn-logo-text">
              <p className="cn-logo-title">Red Setter Homes</p>
              <p className="cn-logo-subtitle">Client Portal</p>
            </div>
          </Link>

          {/* Navigation Links — center */}
          <div className="cn-desktop-links">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`cn-desktop-link ${
                  isActive(link.path) ? "active" : ""
                }`}
              >
                <span className="cn-link-icon">{link.icon}</span>
                <span className="cn-link-label">{link.label}</span>
              </Link>
            ))}
          </div>

          {/* Profile dropdown — right */}
          <div className="cn-desktop-right">
            <div className="cn-profile-container" ref={profileDesktopRef}>
              <button
                className={`cn-profile-trigger ${
                  profileDropdownOpen ? "open" : ""
                }`}
                onClick={toggleProfileDropdown}
                aria-label="Profile menu"
              >
                <FaUserCircle className="cn-profile-icon" />
                <div className="cn-profile-info">
                  <span className="cn-profile-name">{displayName}</span>
                  <span className="cn-profile-email">{user?.email}</span>
                </div>
                <FaChevronDown
                  className={`cn-profile-chevron ${
                    profileDropdownOpen ? "rotated" : ""
                  }`}
                />
              </button>
              <ProfileDropdown
                isOpen={profileDropdownOpen}
                onClose={() => setProfileDropdownOpen(false)}
                onSignOut={handleSignOut}
                onTakeTour={startCurrentTour}
              />
            </div>
          </div>
        </div>
      </nav>

      {/* ==================== MOBILE TOP BAR ==================== */}
      <div className="cn-mobile-top">
        <Link to="/dashboard" className="cn-mobile-logo-link">
          <img src="/rsh-logo-sq.png" alt="RSH" className="cn-mobile-logo" />
          <div className="cn-mobile-logo-text">
            <p className="cn-mobile-title">Red Setter Homes</p>
            <p className="cn-mobile-subtitle">Client Portal</p>
          </div>
        </Link>

        <div className="cn-mobile-top-right">
          <div className="cn-profile-container" ref={profileMobileRef}>
            <button
              className={`cn-mobile-profile-btn ${
                profileDropdownOpen ? "open" : ""
              }`}
              onClick={toggleProfileDropdown}
              aria-label="Profile menu"
            >
              <FaUserCircle className="cn-mobile-profile-icon" />
              <div className="cn-mobile-profile-info">
                <span className="cn-profile-name">{displayName}</span>
              </div>
              <FaChevronDown
                className={`cn-profile-chevron ${
                  profileDropdownOpen ? "rotated" : ""
                }`}
              />
            </button>
            <ProfileDropdown
              isOpen={profileDropdownOpen}
              onClose={() => setProfileDropdownOpen(false)}
              onSignOut={handleSignOut}
              onTakeTour={startCurrentTour}
            />
          </div>
        </div>
      </div>

      {/* ==================== MOBILE BOTTOM NAV ==================== */}
      <nav className="cn-mobile-bottom">
        {navLinks.map((link) => (
          <Link
            key={link.path}
            to={link.path}
            className={`cn-nav-item ${isActive(link.path) ? "active" : ""}`}
          >
            <span className="cn-nav-icon">{link.icon}</span>
            <span className="cn-nav-label">{link.label}</span>
          </Link>
        ))}
      </nav>
    </div>
  );
}

/**
 * Mobile footer component — retained as a named export for any pages
 * that still render it directly. Sign out is now available from the
 * navbar profile dropdown, so this may be safe to remove.
 *
 * @param {Object} props
 * @param {Object} props.user - Current Firebase user
 * @returns {JSX.Element} Mobile footer
 */
export function MobileFooter({ user }) {
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      Sentry.setUser(null);
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
      Sentry.captureException(error);
    }
  };

  return (
    <div className="cn-mobile-footer">
      <div className="cn-mobile-footer-user">
        <div className="cn-mobile-footer-details">
          <span className="cn-mobile-footer-name">
            Logged in as{" "}
            <span className="cn-mobile-footer-email">{user?.email}</span>
          </span>
        </div>
      </div>
      <button onClick={handleSignOut} className="cn-mobile-footer-signout">
        <FaSignOutAlt />
        <span>Sign Out</span>
      </button>
    </div>
  );
}

export default Navbar;
